import React, { useState } from 'react';
import OwnerLayout from '../../components/OwnerLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { api, errMsg, useApi } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { money } from '../../lib/constants';
import RentPaymentsDialog from '../../components/RentPaymentsDialog';
import { Button } from '../../components/ui/button';
import { AlertTriangle, Users, Euro, Store, Banknote } from 'lucide-react';
import RenewalAlerts from '../../components/RenewalAlerts';

const STATUS = {
  ok: { label: 'Paid up', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
  due_soon: { label: 'Renewal due soon', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  expired: { label: 'Expired — fee due', cls: 'bg-rose-500/20 text-rose-300 border-rose-500/30' },
  suspended: { label: 'Suspended', cls: 'bg-slate-500/20 text-slate-300 border-slate-500/30' },
};

export default function OwnerReportPage() {
  const { data: r, loading, reload } = useApi('/owner/report');
  const [payShop, setPayShop] = useState(null);
  const { toast } = useToast();
  const emailReminder = async (s) => {
    try { await api.post(`/owner/shops/${s.id}/send-reminder`); toast({ title: 'Reminder emailed', description: `Sent to ${s.adminEmail} with a copy to you.` }); }
    catch (e) { toast({ title: 'Could not send', description: errMsg(e), variant: 'destructive' }); }
  };

  return (
    <OwnerLayout>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Revenue report</h1>
          <p className="text-slate-400 mt-1">{r ? r.month : '…'} — yearly subscriptions, renewals due and customers per shop.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-8" data-testid="owner-report-stats">
        <Stat icon={Euro} label="Yearly revenue (active)" value={r ? `€${money(r.yearlyRevenue)}` : '—'} tone="text-emerald-400" testId="report-yearly-revenue" />
        <Stat icon={Banknote} label={r ? `Collected in ${r.year}` : 'Collected this year'} value={r ? `€${money(r.collectedThisYear)}` : '—'} hint={r ? `€${money(r.collectedThisMonth)} this month • €${money(r.totalCollected)} all time` : ''} tone="text-emerald-300" testId="report-collected" />
        <Stat icon={AlertTriangle} label="Fees due now" value={r ? `€${money(r.feeDue)}` : '—'} hint={r ? `${r.expired.length} expired • ${r.expiringSoon.length} due within 30 days` : ''} tone="text-amber-300" testId="report-fee-due" />
        <Stat icon={Store} label="Active shops" value={r ? `${r.activeShops} / ${r.totalShops}` : '—'} tone="text-white" testId="report-active-shops" />
        <Stat icon={Users} label="Total customers" value={r ? r.totalCustomers : '—'} tone="text-sky-300" testId="report-total-customers" />
      </div>

      <RenewalAlerts shops={r?.shops} onPay={setPayShop} onEmail={emailReminder} />

      <Card className="mt-8 border-slate-800 bg-slate-950/40 text-slate-100">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="owner-report-table">
              <thead className="text-slate-400 text-xs uppercase tracking-wide">
                <tr>{['Shop', 'Fee / year', 'Active until', 'Days left', 'Overdue', 'Customers', 'Staff', 'Status', ''].map(h => <th key={h} className="text-left px-5 py-3 font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {(r?.shops || []).map(s => (
                  <tr key={s.id} data-testid={`report-row-${s.id}`}>
                    <td className="px-5 py-3.5"><div className="font-semibold">{s.name}</div><div className="text-xs text-slate-400 font-mono">{s.adminUsername}</div></td>
                    <td className="px-5 py-3.5 font-mono text-amber-300">€{money(s.yearlyFee)}</td>
                    <td className="px-5 py-3.5 font-mono">{s.activeUntil}</td>
                    <td className={`px-5 py-3.5 font-mono ${s.daysLeft < 0 ? 'text-rose-300' : s.daysLeft <= 30 ? 'text-amber-300' : 'text-slate-200'}`}>{s.daysLeft}</td>
                    <td className="px-5 py-3.5 font-mono">{s.overdueDays > 0 ? <span className="text-rose-300">{s.overdueDays} d</span> : <span className="text-slate-500">—</span>}</td>
                    <td className="px-5 py-3.5 font-mono">{s.customersCount}</td>
                    <td className="px-5 py-3.5 font-mono">{s.staffCount}/{s.maxStaff}</td>
                    <td className="px-5 py-3.5"><Badge className={STATUS[s.feeStatus].cls}>{STATUS[s.feeStatus].label}</Badge></td>
                    <td className="px-5 py-3.5 text-right"><Button data-testid={`report-pay-${s.id}`} size="sm" variant="outline" onClick={() => setPayShop(s)} className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white"><Banknote className="h-4 w-4 mr-1.5" /> Payments</Button></td>
                  </tr>
                ))}
                {!loading && r?.shops.length === 0 && <tr><td colSpan={9} className="px-5 py-14 text-center text-slate-500">No shops yet.</td></tr>}
                {loading && <tr><td colSpan={9} className="px-5 py-14 text-center text-slate-500">Loading…</td></tr>}
              </tbody>
              {r?.shops.length > 0 && (
                <tfoot className="border-t border-slate-700 text-slate-200">
                  <tr>
                    <td className="px-5 py-3 font-semibold">Totals</td>
                    <td className="px-5 py-3 font-mono font-semibold text-amber-300">€{money(r.shops.reduce((a, s) => a + s.yearlyFee, 0))}</td>
                    <td colSpan={3} />
                    <td className="px-5 py-3 font-mono font-semibold">{r.totalCustomers}</td>
                    <td className="px-5 py-3 font-mono font-semibold">{r.shops.reduce((a, s) => a + s.staffCount, 0)}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-8 border-slate-800 bg-slate-950/40 text-slate-100">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-slate-800 font-semibold">Recent subscription payments</div>
          <table className="w-full text-sm" data-testid="report-payments-table">
            <thead className="text-slate-400 text-xs uppercase tracking-wide"><tr>{['Paid on', 'Shop', 'Amount', 'Years', 'Covers', 'Method'].map(h => <th key={h} className="text-left px-5 py-3 font-semibold">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-800">
              {(r?.recentPayments || []).map(p => (
                <tr key={p.id}>
                  <td className="px-5 py-3 font-mono text-xs">{p.paidOn}</td>
                  <td className="px-5 py-3 font-medium">{p.shopName}</td>
                  <td className="px-5 py-3 font-mono text-emerald-300">€{money(p.amount)}</td>
                  <td className="px-5 py-3 font-mono">{p.years ?? (p.months ? (p.months / 12).toFixed(1) : '—')}</td>
                  <td className="px-5 py-3 font-mono text-xs text-slate-400">{p.periodStart} → {p.periodEnd}</td>
                  <td className="px-5 py-3 text-slate-300">{p.method}</td>
                </tr>
              ))}
              {r && r.recentPayments.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No payments recorded yet.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <RentPaymentsDialog shop={payShop} onClose={() => setPayShop(null)} onChanged={reload} />
    </OwnerLayout>
  );
}

function Stat({ icon: Icon, label, value, hint, tone, testId }) {
  return (
    <Card className="border-slate-800 bg-slate-950/40"><CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
        <Icon className="h-4 w-4 text-slate-500" />
      </div>
      <div className={`text-3xl font-bold mt-2 font-mono ${tone}`} data-testid={testId}>{value}</div>
      {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </CardContent></Card>
  );
}
